import { i18next } from '@yola/ws-sdk';
import { Icon, designSystem } from '@yola/ws-ui';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import ReactModal from 'react-modal';
import { connect } from 'react-redux';
import upsell from 'yola-editor/src/js/modules/upsell';
import user from 'yola-editor/src/js/modules/user';
import ConfettiAnimation from '../../animation/components/confetti-animation';
import actions from '../actions';

const { Paragraph } = designSystem;

class PublishSuccessDialogContainer extends React.Component {
  constructor(props) {
    super(props);

    this.handleUpsellClick = this.handleUpsellClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  handleUpsellClick() {
    const { isWhiteLabel, startB2CDomainPurchase, startWLDomainPurchase } = this.props;
    const triggerId = upsell.constants.triggerIds.PUBLISHING_POPUP;

    if (isWhiteLabel) {
      startWLDomainPurchase(triggerId);
    } else {
      startB2CDomainPurchase(triggerId);
    }
  }

  handleClose(...args) {
    const { onDialogCancel, onClose } = this.props;
    onDialogCancel();
    onClose(...args);
  }

  render() {
    const {
      withUpsell,
      siteUrl,
      isFreeDomainAvailable,
      isWhiteLabel,
      isThirdPartyDomain,
      hasUntranslatedCustomCode,
    } = this.props;

    const shouldDisplayFreeDomainInfo = !isWhiteLabel && isFreeDomainAvailable;
    const shouldDisplayFreeDomainUpsell = withUpsell && shouldDisplayFreeDomainInfo;
    const shouldDisplayCustomDomainUpsell = withUpsell && !shouldDisplayFreeDomainInfo;

    const dialogClasses = classNames('dialog', 'dialog--publish-success', {
      'dialog--with-upsell': withUpsell,
    });

    return (
      <ReactModal
        isOpen
        onRequestClose={this.handleClose}
        className="modal-content"
        overlayClassName="publish-success-dialog-overlay"
        defaultStyles={{ content: {} }}
      >
        <div className="publish-success-dialog-overlay-animation">
          <ConfettiAnimation />
        </div>
        <div className={dialogClasses}>
          <div className="dialog__content">
            <div className="dialog-inner-content">
              <div className="dialog-header">
                <div onClick={this.handleClose} className="dialog__close">
                  <span className="link link--medium link--underline">{i18next.t('Close')}</span>
                </div>
              </div>
              <div className="dialog-body">
                <div className="dialog-body__content">
                  {!withUpsell && (
                    <span className="dialog-body__emoji" role="img" aria-label="Party Popper">
                      &#x1f389;
                    </span>
                  )}
                  <p className="dialog-body__text">
                    {i18next.t('You did it! Your website is now online here:')}
                  </p>
                  <a
                    href={siteUrl}
                    className="link link--large link--underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="link-text">{siteUrl}</span>
                    <span className="link-icon">
                      <Icon glyph="external-link" size="18" />
                    </span>
                  </a>
                  {isThirdPartyDomain && (
                    <div className="third-party-domain-warning">
                      <Paragraph size="small" appearance="inherit">
                        {i18next.t(
                          "Please note: connecting your domain may take a few hours, during which a security certificate warning may appear. If your site isn't visible after 24 hours, contact customer support."
                        )}
                      </Paragraph>
                    </div>
                  )}
                  {hasUntranslatedCustomCode && (
                    <div className="third-party-domain-warning">
                      <Paragraph size="small" appearance="inherit">
                        {i18next.t(
                          "We couldn't auto-translate some custom code on your pages due to a sub-processor issue. Please try again or contact support if it keeps happening."
                        )}
                      </Paragraph>
                    </div>
                  )}
                </div>
                {shouldDisplayFreeDomainUpsell && (
                  <div className="additional-info">
                    <div className="freedomain">
                      <h3 className="freedomain__title">
                        {i18next.t('Your annual plan includes a free custom domain')}
                      </h3>
                      <p className="freedomain__text">
                        {i18next.t(
                          'Websites with custom domains do better on Google. Ready to take your website to the next level?'
                        )}
                      </p>
                      <button type="button" className="btn" onClick={this.handleUpsellClick}>
                        {i18next.t('Get free domain')}
                      </button>
                    </div>
                  </div>
                )}
                {shouldDisplayCustomDomainUpsell && (
                  <div className="additional-info">
                    <div className="upsell">
                      <h3 className="upsell__title">
                        {i18next.t('Ready to take your website to the next level?')}
                      </h3>
                      <p className="upsell__text">
                        {i18next.t(
                          'Do you know that websites with custom domains do better on Google? Add your custom domain and double your site visitors!'
                        )}
                      </p>
                      <button type="button" className="btn" onClick={this.handleUpsellClick}>
                        {i18next.t('Get your domain')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ReactModal>
    );
  }
}

PublishSuccessDialogContainer.propTypes = {
  onClose: PropTypes.func.isRequired,
  startB2CDomainPurchase: PropTypes.func.isRequired,
  startWLDomainPurchase: PropTypes.func.isRequired,
  withUpsell: PropTypes.bool,
  siteUrl: PropTypes.string.isRequired,
  isWhiteLabel: PropTypes.bool.isRequired,
  isFreeDomainAvailable: PropTypes.bool.isRequired,
  isThirdPartyDomain: PropTypes.bool,
  hasUntranslatedCustomCode: PropTypes.bool,
  onDialogCancel: PropTypes.func.isRequired,
};

PublishSuccessDialogContainer.defaultProps = {
  withUpsell: false,
  isThirdPartyDomain: false,
  hasUntranslatedCustomCode: false,
};

const mapStateToProps = (state) => ({
  isWhiteLabel: user.selectors.getWhiteLabelStatus(state),
});

const mapDispatchToProps = (dispatch) => ({
  onClose: () => dispatch(actions.hide()),
  startB2CDomainPurchase: (triggerId) => dispatch(upsell.actions.startB2CDomainPurchase(triggerId)),
  startWLDomainPurchase: (triggerId) => dispatch(upsell.actions.startWLDomainPurchase(triggerId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PublishSuccessDialogContainer);
